import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { ArrowRight, QrCode } from "lucide-react";
import { toast } from "sonner";

// Strip everything that isn't a letter or digit; uppercase. Claim codes are 8 chars.
function normalize(raw) {
  return (raw || "").replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
}

export default function ClaimEntry() {
  const nav = useNavigate();
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const c = normalize(code);
    if (c.length < 6) {
      toast.error("That doesn't look like a valid claim code.");
      return;
    }
    setSubmitting(true);
    nav(`/claim/${c}`);
  };

  return (
    <Layout>
      <section className="min-h-[78vh] flex items-center px-6 py-16">
        <div className="max-w-xl mx-auto w-full text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 border border-[#C9A055]/40 rounded-full mb-6">
            <QrCode size={20} className="text-[#C9A055]" />
          </div>

          <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055] uppercase mb-3">
            Claim Your Truth
          </div>
          <h1 className="font-display text-4xl sm:text-5xl text-[#F4F1EA] leading-[1.05]">
            Enter your claim code
          </h1>
          <p className="font-body text-[15px] text-[#A3A3A3] mt-5 max-w-md mx-auto">
            Your code is printed on the postcard or sticker that came with
            your Simple Truth. Enter it below to register your print and
            add your name to the Truth Registry.
          </p>

          <form onSubmit={submit} className="mt-10 space-y-4" data-testid="claim-entry-form">
            <input
              autoFocus
              data-testid="claim-entry-code"
              value={code}
              onChange={(e) => setCode(normalize(e.target.value))}
              maxLength={12}
              placeholder="ABCD1234"
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              inputMode="text"
              className="w-full bg-transparent border border-white/15 focus:border-[#C9A055] outline-none text-center font-mono tracking-[0.4em] text-2xl sm:text-3xl text-[#F4F1EA] py-5 placeholder:text-[#3a3631]"
            />
            <button
              type="submit"
              disabled={submitting || normalize(code).length < 6}
              data-testid="claim-entry-submit"
              className="btn-gold w-full py-4 inline-flex items-center justify-center gap-2 disabled:opacity-40"
            >
              {submitting ? "Loading…" : (<>Continue <ArrowRight size={15} /></>)}
            </button>
          </form>

          <div className="mt-10 pt-8 border-t border-white/10 text-left max-w-md mx-auto space-y-3">
            <div className="font-ui text-[10px] tracking-[0.28em] text-[#8a857d] uppercase">
              Where do I find my code?
            </div>
            <ul className="font-body text-sm text-[#B5B0A7] space-y-2 list-disc pl-5">
              <li>On the back of your print, on the small certificate card.</li>
              <li>On the postcard or sticker that came in your package.</li>
              <li>In your purchase confirmation email from Simple Truths Club.</li>
            </ul>
            <p className="font-body text-[13px] text-[#8a857d] pt-2">
              Lost your code? Email{" "}
              <a href="mailto:hello@simpletruthsclub.com" className="text-[#C9A055] hover:underline">
                hello@simpletruthsclub.com
              </a>{" "}
              and we'll help you out.
            </p>
          </div>
        </div>
      </section>
    </Layout>
  );
}
